import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Warehouse, Plus, ArrowLeftRight, Trash2 } from "lucide-react";

export default function WarehousesPage() {
  const { token, isAdmin } = useAuth();
  const [warehouses, setWarehouses] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", location: "" });
  const [transferModal, setTransferModal] = useState(false);
  const [tForm, setTForm] = useState({ from_warehouse_id: "", to_warehouse_id: "", product_id: "", quantity: "", notes: "" });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const [wRes, tRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/warehouses`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/warehouses/transfers`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/products`, { headers, withCredentials: true }),
      ]);
      setWarehouses(wRes.data);
      setTransfers(tRes.data);
      setProducts(pRes.data);
    } catch { toast.error("Erro ao carregar armazéns"); }
    finally { setLoading(false); }
  };

  const createWarehouse = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/warehouses`, form, { headers, withCredentials: true });
      toast.success("Armazém criado");
      setModal(false); setForm({ name: "", location: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const removeWarehouse = async (w) => {
    if (!window.confirm(`Remover armazém "${w.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/warehouses/${w.warehouse_id}`, { headers, withCredentials: true });
      toast.success("Removido");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const doTransfer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/warehouses/transfer`, {
        ...tForm,
        from_warehouse_id: tForm.from_warehouse_id || null,
        quantity: parseFloat(tForm.quantity) || 0,
      }, { headers, withCredentials: true });
      toast.success("Transferência registada");
      setTransferModal(false); setTForm({ from_warehouse_id: "", to_warehouse_id: "", product_id: "", quantity: "", notes: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro na transferência"); }
  };

  return (
    <div className="space-y-6" data-testid="warehouses-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Armazéns</h1>
          <p className="text-muted-foreground">Locais de armazenamento e transferências de mercadoria</p>
        </div>
        <div className="flex gap-2">
          {warehouses.length > 0 && (
            <Button variant="outline" onClick={() => setTransferModal(true)} data-testid="warehouse-transfer-btn">
              <ArrowLeftRight className="w-4 h-4 mr-2" /> Transferência
            </Button>
          )}
          <Button onClick={() => setModal(true)} data-testid="add-warehouse-btn">
            <Plus className="w-4 h-4 mr-2" /> Novo Armazém
          </Button>
        </div>
      </div>

      {loading ? <p className="text-muted-foreground">A carregar...</p> :
        warehouses.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">
            <Warehouse className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Nenhum armazém registado
          </CardContent></Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-3">
            {warehouses.map((w) => (
              <Card key={w.warehouse_id} data-testid={`warehouse-card-${w.warehouse_id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{w.name}</p>
                    {w.location && <p className="text-xs text-muted-foreground">{w.location}</p>}
                  </div>
                  {isAdmin && (
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => removeWarehouse(w)}><Trash2 className="w-4 h-4" /></Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      <Card>
        <CardContent className="p-0 divide-y max-h-[400px] overflow-y-auto">
          <p className="p-3 text-xs font-bold uppercase text-muted-foreground">Transferências recentes</p>
          {transfers.length === 0 && <p className="p-4 text-center text-muted-foreground text-sm">Sem transferências</p>}
          {transfers.map((t) => (
            <div key={t.transfer_id} className="flex justify-between items-center p-3 text-sm">
              <div>
                <p className="font-medium">{t.quantity}x {t.product_name}</p>
                <p className="text-xs text-muted-foreground">
                  {t.from_warehouse_name || "Stock geral"} → {t.to_warehouse_name} · {formatDate(t.created_at)} · {t.created_by_name}
                </p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Novo Armazém</DialogTitle></DialogHeader>
          <form onSubmit={createWarehouse} className="space-y-3">
            <div className="space-y-1">
              <Label>Nome *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="warehouse-name-input" />
            </div>
            <div className="space-y-1">
              <Label>Localização</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-warehouse-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={transferModal} onOpenChange={setTransferModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Transferência de Mercadoria</DialogTitle></DialogHeader>
          <form onSubmit={doTransfer} className="space-y-3">
            <div className="space-y-1">
              <Label>Produto *</Label>
              <Select value={tForm.product_id} onValueChange={(v) => setTForm({ ...tForm, product_id: v })}>
                <SelectTrigger data-testid="transfer-product-select"><SelectValue placeholder="Selecionar produto" /></SelectTrigger>
                <SelectContent>{products.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>De (origem)</Label>
              <Select value={tForm.from_warehouse_id} onValueChange={(v) => setTForm({ ...tForm, from_warehouse_id: v })}>
                <SelectTrigger><SelectValue placeholder="Stock geral" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.warehouse_id} value={w.warehouse_id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Para (destino) *</Label>
              <Select value={tForm.to_warehouse_id} onValueChange={(v) => setTForm({ ...tForm, to_warehouse_id: v })}>
                <SelectTrigger data-testid="transfer-to-warehouse-select"><SelectValue placeholder="Armazém destino" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.warehouse_id} value={w.warehouse_id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Quantidade *</Label>
              <Input type="number" step="0.01" min="0.01" required value={tForm.quantity} onChange={(e) => setTForm({ ...tForm, quantity: e.target.value })} data-testid="transfer-qty-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTransferModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="confirm-warehouse-transfer-btn">Transferir</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
